import type React from 'react';
import {
  PackageCheck,
  PackageOpen,
  CheckCircle2,
} from 'lucide-react';
import { OrderStatus, OrderStatusLabels } from '@/types/enums';
import type { OrderStatus as OrderStatusType } from '@/types/enums';
import { cn } from '@/lib/utils';

interface StatusTimelineProps {
  currentStatus: OrderStatusType;
  className?: string;
}

const timelineSteps: Array<{
  status: OrderStatusType;
  label: string;
  icon: React.FC<{ className?: string }>;
}> = [
  { status: OrderStatus.ARRIVED, label: OrderStatusLabels.ARRIVED, icon: PackageCheck },
  { status: OrderStatus.PARTIALLY_HANDED_OFF, label: OrderStatusLabels.PARTIALLY_HANDED_OFF, icon: PackageOpen },
  { status: OrderStatus.COMPLETED, label: OrderStatusLabels.COMPLETED, icon: CheckCircle2 },
];

function getStepIndex(status: OrderStatusType): number {
  if (status === OrderStatus.COMPLETED) return 2;
  if (status === OrderStatus.PARTIALLY_HANDED_OFF) return 1;
  return 0; // ARRIVED
}

const StatusTimeline: React.FC<StatusTimelineProps> = ({ currentStatus, className }) => {
  const currentIdx = getStepIndex(currentStatus);

  return (
    <div className={cn('flex items-start', className)}>
      {timelineSteps.map((step, idx) => {
        const isCompleted = idx < currentIdx;
        const isCurrent = idx === currentIdx;
        const isAfter = idx > currentIdx;
        const Icon = step.icon;

        return (
          <div key={step.status} className="flex-1 flex flex-col items-center relative">
            {idx > 0 && (
              <div
                className={cn(
                  'absolute top-5 right-1/2 w-full h-0.5 -translate-y-1/2',
                  isCompleted || isCurrent ? 'bg-blue-400' : 'bg-gray-200',
                )}
              />
            )}

            <div
              className={cn(
                'relative z-10 w-10 h-10 rounded-full flex items-center justify-center transition-all duration-300',
                isCompleted && 'bg-green-500 text-white shadow-sm',
                isCurrent && 'bg-blue-500 text-white ring-4 ring-blue-100 shadow-md',
                isAfter && 'bg-gray-100 text-gray-400',
              )}
            >
              <Icon className="w-4 h-4" />
            </div>

            <span
              className={cn(
                'mt-2 text-[11px] font-medium text-center leading-tight max-w-[72px]',
                (isCompleted || isCurrent) ? 'text-gray-900' : 'text-gray-400',
              )}
            >
              {step.label}
            </span>
          </div>
        );
      })}
    </div>
  );
};

export default StatusTimeline;
